import { APIGet, APIPost, APIPut, APIDelete, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";
import { IInventoryItem } from "pages/secure/client/product/Product";
import { IProduct, IVariant } from "redux/reducers/models/product";

import { TExtendedProductFilters } from "../../facilityAdmin/product/product";

const apiVersion = "2024-04";
const adminType = "client-admin";
const baseProductUrl = "/" + apiVersion + "/" + adminType + "/product";

// interface IGetProduct {
//   id?: number;
//   search?: string;
//   extended?: boolean;
//   extended_variants?: boolean;
//   sales_channels?: boolean;
//   inventory?: boolean;
//   limit?: number;
//   offset?: number;
//   type?: string;
//   types?: any;
//   all?: boolean;
//   filters?: {
//     label: ExtendedClientProductFilterKey;
//     value: string[] | number[];
//   }[];ß
// }

export type TGetProduct = {
  all?: boolean;
  id?: string | number;
  ids?: Array<number>;
  search?: string;
  filters?: {
    label: keyof TExtendedProductFilters;
    value: string[] | number[];
  }[];
  type?: string;
  types?: string[];
  track_inventory?: boolean;
  folder_id?: string | number;
  variants?: boolean;
  /** This is a taxing call to make. */
  extended?: boolean;
  extended_variants?: boolean;
  inventory?: boolean;
  sales_channels?: boolean;
  modifier_groups?: boolean;
  group_memberships?: boolean;
  /** Default: 50 */
  limit?: number;
  /** Default: 0 */
  offset?: number;
  /** Default: 0 */
  archived?: 0 | 1;
  vendor_id?: number;
  skus?: Array<string>;
};

export type TPostProduct = {
  title?: string;
  /** Default: null */
  subtitle?: string;
  /** Default: null */
  preferred_title?: string;
  type?: string;
  description?: string;
  internal_description?: string;
  vendor_id?: number;
  /** Default = 0 */
  customer_required?: boolean;
  /** Default = 0 */
  shipping_required?: boolean;
  /** Default = 0 */
  fulfillment_required?: boolean;
  /** Default = 0 */
  track_inventory?: boolean;
  /** Default = 0 */
  gift_card?: boolean;
  /** Default = null */
  available_on?: Date;
  /** Default = null */
  unavailable_on?: Date;
  /** Default = facility */
  applicable_taxes?: "facility" | "customer";
  department_id?: number | string;
  category_id?: number | string;
  subcategory_id?: number | string;
  accounting_reference_id?: number | string;
  account_id?: number | string;
  price?: number;
  original_price?: number;
  cost?: number;
  /** Default = 1 */
  taxable?: boolean;
  barcode?: string | null;
  sku?: string | null;
  /** Default = 0 */
  tracked?: boolean;
  tax_line_ids?: number[];
  options?: { name: string; values: string[] }[];
  meta?: TProductMetaData;
  auto_generate_sku?: boolean;
  product_unit_id?: number | string;
  gift_card_restrict_facility?: boolean;
  dynamic_combo?: boolean;
  dynamic_combo_quantity?: number;
};

export type TPutProduct = {
  id: string | number;
  title?: string;
  subtitle?: string;
  /** Default = null */
  preferred_title?: string;
  type?: string;
  description?: string;
  internal_description?: string;
  vendor_id?: number;
  customer_required?: boolean;
  shipping_required?: boolean;
  fulfillment_required?: boolean;
  track_inventory?: boolean;
  gift_card?: boolean;
  available_on?: Date | string; //Date string
  unavailable_on?: Date | string; // Date string
  applicable_taxes?: string; // "facility" | "customer"
  department_id?: number;
  category_id?: number;
  subcategory_id?: number;
  accounting_reference_id?: string | number;
  account_id?: number;
  price?: number;
  original_price?: number;
  cost?: number;
  barcode?: string | null;
  sku?: string | null;
  meta?: {
    kitchen_location_id?: number;
    kitchen_location_id_2?: number;
    kitchen_location_id_3?: number;
    button_color?: string;
    cannot_be_sold?: boolean;
  };
  taxable?: boolean;
  product_unit_id?: number;
  gift_card_restrict_facility?: boolean;
  dynamic_combo?: boolean;
  dynamic_combo_quantity?: number;
};

export type TProductMetaData = {
  membership_type?: string;
  /** Default = false */
  reciprocal?: boolean;
  expiry?: Date | string;
  customer_type_id?: number | null;
};

export interface IVariantToAttach {
  variant_id: number;
  price?: number;
  cost?: number;
  quantity?: number;
}

export async function GetProduct(params: TGetProduct, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<IProduct[]>(baseProductUrl, params, useGlobalLoader, token);
}

// Archive Product
export async function PutArchiveProduct(params: any, useGlobalLoader: boolean) {
  return await APIPut(baseProductUrl + "/archive", params, useGlobalLoader);
}

// POST Add Product to Variant.  Used with combo products
export async function AddProductsToVariant(
  params: { base_variant_id: number; variants_to_attach: IVariantToAttach[] },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.ClientAdmin, "/product/combination"), params, useGlobalLoader);
}

/** Update a product combo price | cost */
export async function UpdateComboProduct(
  params: { base_variant_id: number; variants_to_attach: IVariantToAttach[] },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.ClientAdmin, "/product/combination"), params, useGlobalLoader);
}

export function GetComboVariants(
  params: { base_variant_id: number },
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
) {
  return APIGet<IVariant>(
    APIUrl(AdminType.ClientAdmin, "/product/combination/variants"),
    params,
    useGlobalLoader,
    cancelToken,
  );
}

export async function UpdateInventoryLevels(params: any, useGlobalLoader: boolean) {
  return await APIPut(baseProductUrl + "/inventory/level", params, useGlobalLoader);
}

export async function GetInventoryLevels(params: any, useGlobalLoader: boolean) {
  return await APIGet(baseProductUrl + "/inventory/level", params, useGlobalLoader);
}

export async function PostProduct(params: TPostProduct, useGlobalLoader: boolean) {
  return await APIPost<IProduct>(APIUrl(AdminType.ClientAdmin, "/product"), params, useGlobalLoader);
}

export async function PutProduct(params: TPutProduct, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.ClientAdmin, "/product"), params, useGlobalLoader);
}

// GET Variant
interface IGetVariant {
  id?: number;
  product_id?: number;
  extended?: boolean;
  types?: any;
  combination_variants?: boolean;
}

export async function GetAllVariant(params?: IGetVariant, useGlobalLoader?: boolean) {
  return await APIGet(APIUrl(AdminType.ClientAdmin, "/product/variant"), params, useGlobalLoader);
}

// PUT Variant
export async function PostVariant(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.ClientAdmin, "/product/variant"), params, useGlobalLoader);
}

// PUT Variant
export async function PutVariant(params: any, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.ClientAdmin, "/product/variant"), params, useGlobalLoader);
}

// PUT Archive Variant
export async function ArchiveVariant(params: any, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.ClientAdmin, "/product/variant/archive"), params, useGlobalLoader);
}

// Duplicate Variant
export async function DuplicateVariant(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.ClientAdmin, "/product/variant/duplicate"), params, useGlobalLoader);
}

// GET Product Options
export async function GetProductOptions(
  params?: { product_id: string | number },
  useGlobalLoader?: boolean,
  cancelToken?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.ClientAdmin, "/product/option"), params, useGlobalLoader, cancelToken);
}

// GET product tax lines
export async function GetTaxLines(useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.ClientAdmin, "/client/tax-line"), useGlobalLoader);
}

export type TRemoveProductFromVariant = {
  /** the base product ID */
  base_variant_id: number;

  /** the variant IDs to remove from the base product */
  variant_id: number[];
};

// Add Products to Variant
export async function AddProductToVariant(
  params: { base_variant_id: number; variants_to_attach: IVariantToAttach[] },
  useGlobalLoader: boolean,
) {
  return await APIPost(APIUrl(AdminType.ClientAdmin, "/product/combination"), params, useGlobalLoader);
}

// DELETE Products from Variant
export async function RemoveProductFromVariant(params: TRemoveProductFromVariant, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.ClientAdmin, "/product/combination"), params, useGlobalLoader);
}

// GET Product Inventory Logs
export async function GetInventoryLogs(params: any, useGlobalLoader: boolean) {
  return await APIGet<IInventoryLog[]>(
    APIUrl(AdminType.ClientAdmin, "/product/inventory/record/log/all"),
    params,
    useGlobalLoader,
  );
}

export interface IInventoryLog {
  id: number;
  inventory_level_id: number;
  inventory_record_id: number;
  admin_user_id: number;
  type: string;
  description: string;
  previous_quantity: number;
  new_quantity: number;
  change: number;
  created_at: string;
  inventory_item: IInventoryItem[];
}

// Duplicate Product
export async function DuplicateProduct(params: { product_id: string | number }, useGlobalLoader: boolean) {
  return await APIPost<IProduct>(APIUrl(AdminType.ClientAdmin, "/product/duplicate"), params, useGlobalLoader);
}
